import { FileCheck2, Upload } from "lucide-react";
import { useRef, useState, type DragEvent, type KeyboardEvent } from "react";

/**
 * ファイルを選ぶ領域。**ドロップでも、クリックして選んでも入れられる**。
 *
 * ブラウザ既定の `<input type="file">` は押せる範囲がボタンの幅しかなく、どこに
 * 入れればよいか分かりにくい。枠線で範囲を示し、**枠の中ならどこを押しても
 * ファイル選択が開く**ようにしてある。
 *
 * input 自体は残したまま隠している。クリックで開くのに必要なうえ、表示を変えても
 * ブラウザのファイル選択を自前で作ることはできないため。
 *
 * 操作できる要素はこの枠 1 つだけにしている。枠の中に「選び直す」ボタンを置くと
 * ボタンの入れ子になり、キーボード操作の順序も分かりにくくなる。選んだあとに
 * もう一度押せば選び直せる。
 *
 * ``multiple`` を立てると複数まとめて受け取る（図面 PDF の一括取り込みなど）。
 * 1 件のときと両方を 1 つの props にすると「file と files のどちらを渡すのか」が
 * 読めなくなるので、型で分けてある。
 */
interface BaseProps {
  /** 項目名。読み上げ用のラベルにも使う。 */
  label: string;
  /** 受け付ける拡張子（``".xlsx"`` のようにカンマ区切り）。 */
  accept: string;
  /** 枠の中に出す補足。 */
  hint?: string;
  /**
   * ラベルを枠の上に出すか。既定は出す。囲みの見出しで項目名が分かる場所では
   * false にする（同じ言葉が 2 回並ぶため）。読み上げ用のラベルは常に付く。
   */
  showLabel?: boolean;
  isDisabled?: boolean;
}

interface SingleProps extends BaseProps {
  multiple?: false;
  /** 選択中のファイル。未選択は null。 */
  file: File | null;
  onSelect: (file: File | null) => void;
}

interface MultipleProps extends BaseProps {
  multiple: true;
  /** 選択中のファイル。未選択は空配列。 */
  files: File[];
  onSelectMany: (files: File[]) => void;
}

export default function FileDropZone(props: SingleProps | MultipleProps) {
  const { label, accept, hint, showLabel = true, isDisabled = false } = props;
  const multiple = props.multiple === true;
  const selected = multiple ? props.files : props.file ? [props.file] : [];
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  // dragenter / dragleave は子要素に入るたびに起きる。深さを数えておかないと、
  // 枠の中で動かしただけで強調表示が消える。
  const depth = useRef(0);

  const extensions = accept
    .split(",")
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);

  const accepts = (name: string) =>
    extensions.length === 0 ||
    extensions.some((extension) => name.toLowerCase().endsWith(extension));

  const emit = (files: File[]) => {
    if (props.multiple) props.onSelectMany(files);
    else props.onSelect(files[0] ?? null);
  };

  const take = (files: File[]) => {
    const rejected = files.filter((f) => !accepts(f.name));
    if (rejected.length > 0) {
      setError(`${extensions.join("・")} のファイルを選んでください。`);
      emit([]);
      return;
    }
    setError(null);
    emit(multiple ? files : files.slice(0, 1));
  };

  const open = () => {
    if (isDisabled) return;
    input.current?.click();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      open();
    }
  };

  const stop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    stop(event);
    depth.current = 0;
    setDragging(false);
    if (isDisabled) return;
    take(Array.from(event.dataTransfer.files ?? []));
  };

  return (
    <div>
      {showLabel && <span className="text-small text-foreground">{label}</span>}
      <div
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-label={`${label}を選択`}
        aria-disabled={isDisabled}
        onClick={open}
        onKeyDown={onKeyDown}
        onDragEnter={(event) => {
          stop(event);
          depth.current += 1;
          setDragging(true);
        }}
        onDragOver={(event) => {
          stop(event);
          event.dataTransfer.dropEffect = "copy";
        }}
        onDragLeave={(event) => {
          stop(event);
          depth.current -= 1;
          if (depth.current <= 0) {
            depth.current = 0;
            setDragging(false);
          }
        }}
        onDrop={onDrop}
        className={[
          showLabel ? "mt-1" : "",
          "flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-4 py-6 text-center transition-colors",
          isDisabled
            ? "cursor-not-allowed border-default-200 bg-default-50 text-default-400"
            : "cursor-pointer",
          dragging
            ? "border-brand-500 bg-brand-50"
            : isDisabled
              ? ""
              : "border-default-300 hover:border-brand-500 hover:bg-default-50",
        ].join(" ")}
      >
        {selected.length > 0 ? (
          <>
            <FileCheck2 size={24} className="text-brand-500" />
            {/* 多いと縦に伸びすぎるので、名前を出すのは 5 件まで。 */}
            {selected.slice(0, 5).map((f) => (
              <span
                key={f.name}
                className="text-sm font-bold break-all text-default-700"
              >
                {f.name}
              </span>
            ))}
            {selected.length > 5 && (
              <span className="text-sm text-default-600">
                ほか {selected.length - 5} 件
              </span>
            )}
            <span className="text-tiny text-default-500">
              選び直すには、もう一度ここを押してください
            </span>
          </>
        ) : (
          <>
            <Upload size={24} className="text-default-400" />
            <span className="text-sm text-default-600">
              ここにファイルをドロップ、またはクリックして選択
              {multiple && "（複数可）"}
            </span>
            {hint && <span className="text-tiny text-default-500">{hint}</span>}
          </>
        )}
      </div>

      {/* クリックで開くために input は残す（表示だけ隠す）。 */}
      <input
        ref={input}
        type="file"
        accept={accept}
        multiple={multiple}
        aria-label={label}
        className="hidden"
        disabled={isDisabled}
        onChange={(event) => {
          take(Array.from(event.target.files ?? []));
          // 値を空に戻す。同じファイルをもう一度選んだときにも change が起きる
          // ようにするため（選び直しのつもりが無反応に見えるのを防ぐ）。
          event.target.value = "";
        }}
      />

      {error && <p className="mt-1 text-sm text-danger">{error}</p>}
    </div>
  );
}
